import { useState, useEffect, useCallback } from 'react';
import useApi from '../useApi';
import axiosInstance from '../axiosInstance';

const BASE = '/geo/countries';

// ─── Plain API (exported for use outside hooks) ─────────────────────────────

export const getCountries = (params = {}) =>
  axiosInstance.get(BASE, { params }).then((res) => res.data);

export const getCountryById = (id) =>
  axiosInstance.get(`${BASE}/${id}`).then((res) => res.data);

export const createCountry = (data) =>
  axiosInstance.post(BASE, data).then((res) => res.data);

export const updateCountry = (id, data) =>
  axiosInstance.put(`${BASE}/${id}`, data).then((res) => res.data);

export const deleteCountry = (id) =>
  axiosInstance.delete(`${BASE}/${id}`).then((res) => res.data);

export const toggleCountryActive = (id) =>
  axiosInstance.patch(`${BASE}/${id}/toggle-active`).then((res) => res.data);

/**
 * uploadCountryImage - upload a main image for a country (multipart/form-data)
 * POST /geo/countries/:id/image  (field name "image")
 * @param {number|string} id - country id (must already exist)
 * @param {File} file - image file
 * @returns API response containing { data: { mainImage } }
 */
export const uploadCountryImage = (id, file) => {
  const formData = new FormData();
  formData.append('image', file);
  return axiosInstance
    .post(`${BASE}/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => res.data);
};

/**
 * removeCountryImage - delete a country's main image
 * DELETE /geo/countries/:id/image  → returns { data: { mainImage: null } }
 */
export const removeCountryImage = (id) =>
  axiosInstance.delete(`${BASE}/${id}/image`).then((res) => res.data);

// ─── Hooks ──────────────────────────────────────────────────────────────────

/**
 * useGetCountries - fetch countries list with params (like useAuth/useApi pattern)
 * @param {Object} params - { page, limit, search, active, sortBy, sortOrder }
 * @returns { data, pagination, loading, error, refetch }
 */
export const useGetCountries = (params = {}) => {
  const {
    page = 1,
    limit = 10,
    search = '',
    active = '',
    sortBy = 'name',
    sortOrder = 'ASC',
  } = params;

  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCountries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = {
        page,
        limit,
        sortBy,
        sortOrder,
      };
      if (search) queryParams.search = search;
      if (active !== '') queryParams.active = active;

      const response = await axiosInstance.get(BASE, { params: queryParams });
      const res = response.data;

      if (res.success) {
        setData(res.data || []);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      } else {
        setError(res.message || 'Failed to load countries');
        setData([]);
      }
    } catch (err) {
      if (err.response) {
        setError(err.response?.data?.message || err.response?.data?.error || `Request failed (${err.response.status})`);
      } else if (err.request) {
        setError('Cannot reach server. Check that the backend is running (e.g. http://localhost:5000).');
      } else {
        setError(err.message || 'Failed to load countries');
      }
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, active, sortBy, sortOrder]);

  useEffect(() => {
    fetchCountries();
  }, [fetchCountries]);

  const refetch = useCallback(() => {
    fetchCountries();
  }, [fetchCountries]);

  return { data, pagination, loading, error, refetch };
};

/**
 * useCreateCountry - create country (useApi pattern like useLogin)
 */
export const useCreateCountry = () => {
  return useApi(BASE, { method: 'POST' });
};

/**
 * useUpdateCountry - update country by id
 * @returns { execute(id, payload), loading, error }
 */
export const useUpdateCountry = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, payload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.put(`${BASE}/${id}`, payload);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update country';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * useDeleteCountry - delete country by id
 */
export const useDeleteCountry = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.delete(`${BASE}/${id}`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete country';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * useUploadCountryImage - upload a main image for a country (multipart/form-data)
 * @returns { execute(id, file), loading, error }
 * POST /geo/countries/:id/image  → returns { data: { mainImage } }
 */
export const useUploadCountryImage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, file) => {
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const response = await axiosInstance.post(`${BASE}/${id}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to upload image';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};

/**
 * useToggleCountryActive - toggle active status
 */
export const useToggleCountryActive = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axiosInstance.patch(`${BASE}/${id}/toggle-active`);
      return response.data;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to toggle status';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
};